import React, { useState, useEffect, useRef } from 'react';
import DecimalRating from '../common/StarRating';

// Comprehensive list of countries
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'England', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos',
  'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi',
  'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova',
  'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia',
  'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Scotland', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Wales', 'Yemen', 'Zambia', 'Zimbabwe'
];

// Country Autocomplete Component
const CountryAutocomplete = ({ value, onChange, placeholder = "Select or type a country..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [filteredCountries, setFilteredCountries] = useState(COUNTRIES);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const filtered = COUNTRIES.filter(country =>
      country.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCountries(filtered);
    setHighlightedIndex(-1);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    onChange(newValue);
  };

  const handleCountrySelect = (country) => {
    setSearchTerm(country);
    onChange(country);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
          handleCountrySelect(filteredCountries[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-4 py-4 pr-10 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white text-gray-700 min-h-[60px] transition-all duration-200"
          autoComplete="off"
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg className={`h-4 w-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredCountries.length > 0 ? (
            filteredCountries.map((country, index) => (
              <button
                key={country}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
                  index === highlightedIndex ? 'bg-blue-50' : ''
                } ${searchTerm === country ? 'bg-blue-100 font-medium' : ''}`}
                onClick={() => handleCountrySelect(country)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {country}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 italic">
              No countries found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to get rating emoji
const getRatingEmoji = (rating) => {
  if (rating >= 9) return '🤩 Masterpiece!';
  if (rating >= 7) return '😍 Loved it!';
  if (rating >= 5) return '😊 Pretty good!';
  if (rating >= 3) return '😐 Meh...';
  if (rating >= 1) return '😤 Bad!';
  return '';
};

const getHateRatingEmoji = (rating) => {
  if (rating <= 2) return '🤮 Awful!';
  if (rating <= 4) return '😤 Bad!';
  if (rating <= 6) return '😐 Meh...';
  return '';
};

export default function OnboardingNew({ user, onDone, onProfileUpdate }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);

  // Profile data
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [services, setServices] = useState([]);
  const [serviceInput, setServiceInput] = useState('');

  // Love review data
  const [lovedFilm, setLovedFilm] = useState('');
  const [lovedRating, setLovedRating] = useState(0);
  const [lovedReview, setLovedReview] = useState('');
  const [lovedPublic, setLovedPublic] = useState(true);

  // Hate review data
  const [hatedFilm, setHatedFilm] = useState('');
  const [hatedRating, setHatedRating] = useState(0);
  const [hatedReview, setHatedReview] = useState('');
  const [hatedPublic, setHatedPublic] = useState(true);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const API_KEY = process.env.REACT_APP_API_KEY;

  const completeOnboarding = async () => {
    setSaving(true);
    setError('');

    try {
      // 1. Submit both reviews
      const reviewPromises = [
        // Loved review
        fetch(`${API_BASE}/v1/reviews/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY
          },
          body: JSON.stringify({
            name: lovedFilm,
            review: lovedReview || null,
            rating: parseFloat(lovedRating),
            user_id: user.id,
            visibility: lovedPublic ? 'public' : 'private',
          })
        }),
        // Hated review
        fetch(`${API_BASE}/v1/reviews/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY
          },
          body: JSON.stringify({
            name: hatedFilm,
            review: hatedReview || null,
            rating: parseFloat(hatedRating),
            user_id: user.id,
            visibility: hatedPublic ? 'public' : 'private',
          })
        })
      ];

      await Promise.all(reviewPromises);

      // 2. Update user profile and complete onboarding
      const response = await fetch(`${API_BASE}/v1/users/${user.id}`, {
        method: 'PATCH',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          onboarding_completed: true,
          username,
          bio,
          location,
          services: services.join(', ')
        })
      });

      if (!response.ok) throw new Error('Failed to complete onboarding.');

      const data = await response.json();
      if (onProfileUpdate) onProfileUpdate(data);

      // 3. Trigger AI recommendation generation
      fetch(`${API_BASE}/v1/ai_recommendations/generate`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-User-ID': user.id
        },
      }).catch(err => console.warn('Error triggering recommendations:', err));

      setSaving(false);
      if (onDone) onDone();
    } catch (e) {
      setError('Failed to complete onboarding. Please try again.');
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl w-full max-w-md text-center">
          <div className="text-gray-700 text-lg">Please login to continue onboarding.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl w-full max-w-md text-center space-y-6 my-auto">

        {/* Step 0: Value Prop */}
        {step === 0 && (
          <>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Take the power back</h2>
            <p className="text-gray-700 text-base mb-6 leading-relaxed text-left">
              We believe that most TV and film recommendations from streaming services are biased, often paid for (probably by lizards), and result in us wasting our time scrolling through trash, or worse... watching trash.
              <br /><br />
              So we built Clingfilm as a way to generate truly personalised recommendations, based on a knowledge of what we actually like and dislike.
              <br /><br />
              It's quite simple:
            </p>
            <ul className="text-left text-gray-700 space-y-2 mb-8">
              <li>• Review films and TV series you've watched</li>
              <li>• We learn your unique taste (we promise we are not lizards)</li>
              <li>• We generate personalized recommendations across the platforms you have access to</li>
              <li>• As a bonus, you can also see what your friends have been watching</li>
            </ul>
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => setStep(1)}
            >
              Enter the Clingfilm
            </button>
          </>
        )}

        {/* Step 1: Location */}
        {step === 1 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(0)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Where Are You Watching From?</h2>
            <p className="text-gray-700 text-base mb-6">
              We'll only recommend films actually available in your country. No more 'not available in your region'.
            </p>
            <CountryAutocomplete
              value={location}
              onChange={setLocation}
              placeholder="Select or type your country..."
            />
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => {
                if (!location) {
                  setError('Please select a country.');
                  return;
                }
                setError('');
                setStep(2);
              }}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 2: Streaming Services */}
        {step === 2 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(1)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Which Streaming Services Do You Use?</h2>
            <p className="text-gray-700 text-base mb-6">
              Tell us your streaming services so we only recommend what you can actually watch.
            </p>
            <div
              className="flex flex-wrap items-center border border-gray-300 rounded-lg px-3 py-2 min-h-[48px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all duration-200 bg-gray-50 mb-6"
              onClick={() => document.getElementById('streaming-services-input')?.focus()}
            >
              {services.map((service, idx) => (
                <span
                  key={idx}
                  className="flex items-center px-3 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full text-sm font-medium mr-2 mb-2 shadow-sm"
                >
                  {service}
                  <button
                    className="ml-2 text-white/80 hover:text-white focus:outline-none font-bold text-lg leading-none"
                    onClick={e => {
                      e.stopPropagation();
                      const newServices = services.filter((_, i) => i !== idx);
                      setServices(newServices);
                    }}
                    type="button"
                    aria-label={`Remove service: ${service}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                id="streaming-services-input"
                type="text"
                value={serviceInput}
                placeholder={services.length === 0 ? "E.g. Netflix, Hulu, Apple TV+" : ''}
                className="flex-1 outline-none border-none bg-white min-w-[120px] py-1 text-sm text-gray-700 placeholder-gray-400"
                onChange={e => setServiceInput(e.target.value)}
                onKeyDown={e => {
                  if ((e.key === 'Enter' || e.key === 'Tab' || e.key === ',') && serviceInput.trim() !== '') {
                    e.preventDefault();
                    if (!services.includes(serviceInput.trim())) {
                      setServices([...services, serviceInput.trim()]);
                      setServiceInput('');
                    }
                  }
                }}
              />
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => {
                if (services.length === 0) {
                  setError('Please add at least one streaming service.');
                  return;
                }
                setError('');
                setStep(3);
              }}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 3: Review Intro */}
        {step === 3 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(2)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">I Remember My First Review</h2>
            <p className="text-gray-700 text-base mb-6 leading-relaxed">
              Ok, now we need a couple of initial reviews (literally just 2!) so we can understand at least a little bit about your taste.
              <br /><br />
              Think of a film or TV series you watched recently that you <strong>really enjoyed</strong>.
            </p>
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => setStep(4)}
            >
              My First Review
            </button>
          </>
        )}

        {/* Step 4: Film Title (Love) */}
        {step === 4 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(3)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">What Did You Love?</h2>
            <p className="text-gray-700 text-base mb-6">
              Type the name of a film or TV show you really enjoyed.
            </p>
            <input
              type="text"
              value={lovedFilm}
              onChange={(e) => setLovedFilm(e.target.value)}
              placeholder="E.g. The Matrix, Breaking Bad, Parasite..."
              className="w-full px-4 py-3 mb-6 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            />
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => {
                if (!lovedFilm.trim()) {
                  setError('Please enter a film or TV show title.');
                  return;
                }
                setError('');
                setStep(5);
              }}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 5: Rating (Love) */}
        {step === 5 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(4)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">How Much Did You Love It?</h2>
            <p className="text-gray-700 text-base mb-6">
              Rate '{lovedFilm}' from 1-10
            </p>
            <DecimalRating
              value={lovedRating}
              onChange={setLovedRating}
              label=""
            />
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => {
                if (lovedRating === 0) {
                  setError('Please rate the film from 1-10.');
                  return;
                }
                setError('');
                setStep(6);
              }}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 6: Quick Thoughts (Love) */}
        {step === 6 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(5)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Why Did You Like It?</h2>
            <p className="text-gray-700 text-base mb-6">
              Tell us what made '{lovedFilm}' special. Your review doesn't have to be long - just a few opinionated words are great at training your recommendations.
            </p>
            <textarea
              value={lovedReview}
              onChange={(e) => setLovedReview(e.target.value)}
              placeholder="The cinematography was stunning, the characters felt real..."
              rows="4"
              className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            />
            <div className="mb-6 flex items-center space-x-2 text-left">
              <input
                type="checkbox"
                id="loved-public"
                checked={lovedPublic}
                onChange={(e) => setLovedPublic(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="loved-public" className="text-gray-700 text-sm">
                Make this review public (it will appear in the community social feed)
              </label>
            </div>
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => setStep(7)}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 7: Film Title (Hate) */}
        {step === 7 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(6)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Ok, What Don't You Like?</h2>
            <p className="text-gray-700 text-base mb-6">
              Think of something you watched recently that was <strong>terrible</strong>.
              <br /><br />
              This is just as important - this helps us know what to avoid.
            </p>
            <input
              type="text"
              value={hatedFilm}
              onChange={(e) => setHatedFilm(e.target.value)}
              placeholder="E.g. I bloody hate Star Wars. All Star Wars is terrible!"
              className="w-full px-4 py-3 mb-6 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            />
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => {
                if (!hatedFilm.trim()) {
                  setError('Please enter a film or TV show title.');
                  return;
                }
                setError('');
                setStep(8);
              }}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 8: Rating + Thoughts (Hate) */}
        {step === 8 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(7)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">How Bad Was '{hatedFilm}'?</h2>
            <p className="text-gray-700 text-base mb-6">
              Don't hold back...
            </p>
            <DecimalRating
              value={hatedRating}
              onChange={setHatedRating}
              label=""
            />
            <textarea
              value={hatedReview}
              onChange={(e) => setHatedReview(e.target.value)}
              placeholder="The plot made no sense, or it was boring, or the acting was terrible..."
              rows="4"
              className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            />
            <div className="mb-6 flex items-center space-x-2 text-left">
              <input
                type="checkbox"
                id="hated-public"
                checked={hatedPublic}
                onChange={(e) => setHatedPublic(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="hated-public" className="text-gray-700 text-sm">
                Make this review public (it will appear in the community social feed)
              </label>
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <button
              className="brand-button text-lg px-8 py-3"
              onClick={() => {
                if (hatedRating === 0) {
                  setError('Please rate the film from 1-10.');
                  return;
                }
                setError('');
                setStep(9);
              }}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 9: Username and Bio */}
        {step === 9 && (
          <>
            <button
              className="text-gray-500 mb-4 underline hover:text-gray-700 cursor-pointer text-left"
              onClick={() => setStep(8)}
            >
              &larr; Back
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Ok, Final Step!</h2>
            <p className="text-gray-700 text-base mb-6">
              Pick a username and a bio for your profile.
            </p>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username (min 3 characters)"
              className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
              minLength={3}
            />
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell us a bit about yourself... (optional)"
              rows="3"
              className="w-full px-4 py-3 mb-6 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
            />
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <button
              className={`brand-button text-lg px-8 py-3 ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (username.length < 3) {
                  setError('Username must be at least 3 characters.');
                  return;
                }
                setError('');
                completeOnboarding();
              }}
              disabled={saving}
            >
              {saving ? 'Setting up...' : 'Enter the Clingfilm!'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}